import type { MessageSend } from '../api/types';

export interface OutboxItem {
  ownerId: string;
  chatId: string;
  body: MessageSend & { clientMsgId: string };
  createdAt: number;
  attempts: number;
  nextAttemptAt: number;
  failed: boolean;
}

export interface OutboxStore {
  list(ownerId: string): Promise<OutboxItem[]>;
  put(item: OutboxItem): Promise<void>;
  remove(ownerId: string, clientMsgId: string): Promise<void>;
}

/** Каждое сообщение — отдельная запись: вкладки не перезаписывают чужие снимки очереди. */
export class IndexedDbOutboxStore implements OutboxStore {
  private database?: Promise<IDBDatabase>;

  private open(): Promise<IDBDatabase> {
    if (!this.database) {
      this.database = new Promise<IDBDatabase>((resolve, reject) => {
        const request = indexedDB.open('homechat-outbox', 1);
        request.onupgradeneeded = () => {
          const store = request.result.createObjectStore('messages', { keyPath: ['ownerId', 'body.clientMsgId'] });
          store.createIndex('owner', 'ownerId');
        };
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
        request.onblocked = () => reject(new Error('IndexedDB заблокирована'));
      }).catch(error => { this.database = undefined; throw error; });
    }
    return this.database;
  }

  private async transaction<T>(mode: IDBTransactionMode, run: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
    const db = await this.open();
    return new Promise((resolve, reject) => {
      const tx = db.transaction('messages', mode);
      const request = run(tx.objectStore('messages'));
      // Успех request ещё не означает фиксации: quota/abort могут случиться позже.
      tx.oncomplete = () => resolve(request.result);
      tx.onabort = () => reject(tx.error ?? new Error('Запись очереди отменена'));
      tx.onerror = () => reject(tx.error ?? request.error);
    });
  }

  async list(ownerId: string): Promise<OutboxItem[]> {
    const items = await this.transaction<OutboxItem[]>('readonly', store => store.index('owner').getAll(ownerId));
    return items.sort((a, b) => a.createdAt - b.createdAt || a.body.clientMsgId.localeCompare(b.body.clientMsgId));
  }

  async put(item: OutboxItem): Promise<void> {
    await this.transaction('readwrite', store => store.put(item));
  }

  async remove(ownerId: string, clientMsgId: string): Promise<void> {
    await this.transaction('readwrite', store => store.delete([ownerId, clientMsgId]));
  }
}
